import React, { ReactElement } from 'react'
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'

type Instructor = {
  id: string
  name: string
  credentials?: string
}

type Props = {
  instructors: Instructor[]
  selectedIDs: string[]
  onChange: (ids: string[]) => void
}

export default function InstructorFilter({
  instructors,
  selectedIDs,
  onChange,
}: Props): ReactElement {
  let label =
    selectedIDs.length === 0
      ? 'All instructors'
      : `${selectedIDs.length} selected`

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: selectedIDs.length > 0 ? 1 : 0,
        }}
      >
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="instructor-filter-label">Instructor</InputLabel>
          <Select
            labelId="instructor-filter-label"
            multiple
            value={selectedIDs}
            label="Instructor"
            onChange={(e) =>
              onChange(
                typeof e.target.value === 'string'
                  ? e.target.value.split(',')
                  : e.target.value,
              )
            }
            renderValue={() => (
              <Typography variant="body2" color="text.secondary">
                {label}
              </Typography>
            )}
          >
            {instructors.map((i) => (
              <MenuItem key={i.id} value={i.id}>
                {i.credentials ? `${i.name} (${i.credentials})` : i.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedIDs.length > 0 && (
          <Typography
            component="button"
            variant="body2"
            color="text.secondary"
            onClick={() => onChange([])}
            sx={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:hover': { color: 'text.primary' },
              whiteSpace: 'nowrap',
            }}
          >
            Clear all
          </Typography>
        )}
      </Box>
      {selectedIDs.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {selectedIDs.map((id) => {
            let instructor = instructors.find((i) => i.id === id)
            return (
              <Chip
                key={id}
                label={
                  instructor
                    ? instructor.credentials
                      ? `${instructor.name} (${instructor.credentials})`
                      : instructor.name
                    : id
                }
                size="small"
                onDelete={() => onChange(selectedIDs.filter((s) => s !== id))}
              />
            )
          })}
        </Box>
      )}
    </Box>
  )
}
